import { useState } from 'react';

import { formatPotdCode } from '@1nl/core';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { PotdResponse } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useClipboardCopy } from '../../components/useClipboardCopy.ts';
import { palette } from '../../theme.ts';

export function PotdCard(): React.JSX.Element {
  const { api } = useAuth();
  const [result, setResult] = useState<PotdResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rotateOpen, setRotateOpen] = useState(false);
  const { copied, copy } = useClipboardCopy();

  const reveal = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await api.potd.get());
    } catch (err) {
      setError(actionError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: palette.textSecondary }}>
          One code per day unlocks owner-gated actions on every shop laptop, e.g. changing the sync endpoint. Valid today (UTC) plus a day either side.
        </Typography>
        {result === null ? (
          <QuietButton disabled={busy} onClick={() => void reveal()}>
            {busy ? 'Revealing...' : "Reveal today's password"}
          </QuietButton>
        ) : (
          <Stack spacing={1}>
            <TextField
              label={`Password for ${result.date} UTC`}
              value={formatPotdCode(result.code)}
              slotProps={{
                htmlInput: {
                  readOnly: true,
                  'aria-label': 'password-of-the-day',
                },
              }}
              sx={{ fontFamily: 'monospace' }}
            />
            <QuietButton onClick={() => void copy(formatPotdCode(result.code))}>{copied ? 'Copied' : 'Copy password'}</QuietButton>
          </Stack>
        )}
        {error && (
          <Alert role="alert" severity="error">
            {error}
          </Alert>
        )}
        <QuietButton onClick={() => setRotateOpen(true)}>Rotate secret</QuietButton>
      </Stack>

      <ConfirmDialog
        open={rotateOpen}
        title="Rotate password-of-the-day secret"
        body="Every shop switches to the new code at its next sync. Rotate after a laptop theft."
        confirmLabel="Rotate secret"
        onConfirm={async () => setResult(await api.potd.rotate())}
        onClose={() => setRotateOpen(false)}
      />
    </Paper>
  );
}

export default PotdCard;
